import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/common";
import { FedexHealthCard } from "@/components/FedexHealthCard";
import { UpsHealthCard } from "@/components/UpsHealthCard";
import { GmailCard } from "@/components/GmailCard";
import { DispatchEmailsCard } from "@/components/DispatchEmailsCard";
import { BankAccountsCard } from "@/components/BankAccountsCard";
import { CompanyProfileCard } from "@/components/CompanyProfileCard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Separator } from "@/components/ui/separator";
import { toast } from "sonner";
import { Megaphone, CaretDown, Bank, Truck, EnvelopeSimple, Plugs, UsersThree, CloudArrowUp, ArrowSquareOut } from "@phosphor-icons/react";

function PortalMessagesCard() {
  const [globalMsg, setGlobalMsg] = useState(null);
  const [clients, setClients] = useState([]);
  const [savingGlobal, setSavingGlobal] = useState(false);
  const [savingId, setSavingId] = useState(null);
  const [q, setQ] = useState("");
  const [openId, setOpenId] = useState(null);

  const load = () =>
    api.get("/settings/portal-messages").then((r) => {
      setGlobalMsg(r.data.global);
      setClients(r.data.clients || []);
    }).catch(() => toast.error("Could not load portal messages"));
  useEffect(() => { load(); }, []);

  const saveGlobal = async () => {
    setSavingGlobal(true);
    try {
      await api.patch("/settings/portal-messages/global", globalMsg);
      toast.success("Global message saved");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to save"); }
    finally { setSavingGlobal(false); }
  };

  const setClient = (id, patch) => setClients((cs) => cs.map((c) => (c.client_id === id ? { ...c, ...patch } : c)));

  const saveClient = async (c) => {
    setSavingId(c.client_id);
    try {
      await api.put(`/settings/portal-messages/client/${c.client_id}`, { text: c.text || "", enabled: !!c.enabled });
      toast.success(`Message for ${c.client_name} saved`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to save"); }
    finally { setSavingId(null); }
  };

  if (!globalMsg) return <div className="py-10 text-center text-muted-foreground">Loading…</div>;

  return (
    <div className="hp-card p-6 space-y-6" data-testid="portal-messages-form">
      <div className="flex items-start gap-2">
        <Megaphone size={20} weight="fill" className="text-primary mt-0.5 shrink-0" />
        <div>
          <h2 className="font-heading font-semibold text-lg">Portal Messages</h2>
          <p className="text-sm text-muted-foreground">Banners shown at the top of the client portal. The global message reaches every client; a client-specific message shows only to that client (in addition to the global one).</p>
        </div>
      </div>

      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <Label className="text-xs uppercase tracking-wide font-semibold">Global message — all clients</Label>
          <div className="flex items-center gap-2">
            <span className="text-xs text-muted-foreground">{globalMsg.enabled ? "Visible" : "Hidden"}</span>
            <Switch checked={globalMsg.enabled} onCheckedChange={(v) => setGlobalMsg({ ...globalMsg, enabled: v })} data-testid="global-message-toggle" />
          </div>
        </div>
        <Textarea
          className="rounded-sm min-h-[80px]"
          placeholder="e.g. Our office will be closed 12–14 June for the public holiday."
          value={globalMsg.text || ""}
          onChange={(e) => setGlobalMsg({ ...globalMsg, text: e.target.value })}
          data-testid="global-message-text"
        />
        <Button className="rounded-sm" onClick={saveGlobal} disabled={savingGlobal} data-testid="global-message-save">
          {savingGlobal ? "Saving…" : "Save global message"}
        </Button>
      </div>

      <Separator />

      <div className="space-y-3">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <Label className="text-xs uppercase tracking-wide font-semibold">Client-specific messages <span className="font-normal normal-case text-muted-foreground">· {clients.filter((c) => c.enabled && c.text?.trim()).length} active</span></Label>
          <Input className="rounded-sm h-9 w-full sm:w-64" placeholder="Search client…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="client-message-search" />
        </div>
        {clients.length === 0 ? (
          <p className="text-sm text-muted-foreground">No clients yet. Add clients to send them a targeted message.</p>
        ) : (
          <div className="divide-y divide-border border border-border rounded-sm">
            {clients.filter((c) => !q.trim() || c.client_name.toLowerCase().includes(q.trim().toLowerCase())).map((c) => {
              const openRow = openId === c.client_id;
              return (
                <div key={c.client_id} data-testid={`client-message-${c.client_id}`}>
                  <button type="button" onClick={() => setOpenId(openRow ? null : c.client_id)} className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left hover:bg-muted/40" data-testid={`client-message-row-${c.client_id}`}>
                    <div className="min-w-0">
                      <p className="font-medium text-sm truncate">{c.client_name}</p>
                      {c.text?.trim() && !openRow && <p className="text-xs text-muted-foreground truncate">{c.text}</p>}
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <span className={`text-[10px] uppercase tracking-wide font-semibold px-2 py-0.5 rounded-full ${c.enabled && c.text?.trim() ? "bg-emerald-50 text-emerald-700" : "bg-muted text-muted-foreground"}`}>{c.enabled && c.text?.trim() ? "Visible" : "Hidden"}</span>
                      <CaretDown size={14} className={`transition-transform ${openRow ? "rotate-180" : ""}`} />
                    </div>
                  </button>
                  {openRow && (
                    <div className="px-4 pb-4 space-y-3">
                      <div className="flex items-center justify-end gap-2">
                        <span className="text-xs text-muted-foreground">{c.enabled ? "Visible" : "Hidden"}</span>
                        <Switch checked={!!c.enabled} onCheckedChange={(v) => setClient(c.client_id, { enabled: v })} data-testid={`client-message-toggle-${c.client_id}`} />
                      </div>
                      <Textarea
                        className="rounded-sm min-h-[64px]"
                        placeholder={`Message shown only to ${c.client_name}`}
                        value={c.text || ""}
                        onChange={(e) => setClient(c.client_id, { text: e.target.value })}
                        data-testid={`client-message-text-${c.client_id}`}
                      />
                      <Button variant="outline" size="sm" className="rounded-sm" onClick={() => saveClient(c)} disabled={savingId === c.client_id} data-testid={`client-message-save-${c.client_id}`}>
                        {savingId === c.client_id ? "Saving…" : "Save"}
                      </Button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

export default function BankSettings() {
  const [params, setParams] = useSearchParams();
  const tab = SECTIONS.some((s) => s.key === params.get("tab")) ? params.get("tab") : "finance";
  const setTab = (k) => setParams({ tab: k }, { replace: true });
  return (
    <div className="space-y-6">
      <PageHeader subtitle="Configuration" title="Settings" />
      <div className="grid lg:grid-cols-[220px_1fr] gap-6 items-start">
        <nav className="lg:sticky lg:top-4 flex lg:flex-col gap-1 overflow-x-auto -mx-4 px-4 lg:mx-0 lg:px-0 pb-1" data-testid="settings-nav">
          {SECTIONS.map((s) => (
            <button key={s.key} type="button" onClick={() => setTab(s.key)} className={`flex items-center gap-2.5 rounded-xl px-3 py-2.5 text-sm font-medium whitespace-nowrap transition-colors ${tab === s.key ? "bg-primary/10 text-primary" : "text-muted-foreground hover:bg-muted hover:text-foreground"}`} data-testid={`settings-tab-${s.key}`}>
              <s.icon size={18} weight={tab === s.key ? "fill" : "regular"} /> {s.label}
            </button>
          ))}
          <div className="hidden lg:block border-t border-border my-2" />
          {LINKS.map((l) => (
            <Link key={l.to} to={l.to} className="flex items-center gap-2.5 rounded-xl px-3 py-2.5 text-sm font-medium whitespace-nowrap text-muted-foreground hover:bg-muted hover:text-foreground" data-testid={`settings-link-${l.key}`}>
              <l.icon size={18} /> {l.label} <ArrowSquareOut size={12} className="ml-auto opacity-60" />
            </Link>
          ))}
        </nav>
        <div className="space-y-6 max-w-3xl min-w-0" data-testid={`settings-section-${tab}`}>
          <div>
            <h2 className="font-heading text-xl font-bold">{SECTIONS.find((s) => s.key === tab).label}</h2>
            <p className="text-sm text-muted-foreground">{SECTIONS.find((s) => s.key === tab).blurb}</p>
          </div>
          {tab === "finance" && <><CompanyProfileCard /><BankAccountsCard /></>}
          {tab === "portal" && <PortalMessagesCard />}
          {tab === "dispatch" && <DispatchEmailsCard />}
          {tab === "email" && <GmailCard />}
          {tab === "carriers" && <><FedexHealthCard /><UpsHealthCard /></>}
        </div>
      </div>
    </div>
  );
}

const SECTIONS = [
  { key: "finance", label: "Bank & Invoices", icon: Bank, blurb: "Bank accounts (USD / EUR / AED) printed on client invoices; pick the default." },
  { key: "portal", label: "Client Portal", icon: Megaphone, blurb: "Announcement banners shown to clients when they log in." },
  { key: "dispatch", label: "Dispatch Team", icon: Truck, blurb: "Who gets notified when a shipment is ready to dispatch." },
  { key: "email", label: "Email Sending", icon: EnvelopeSimple, blurb: "Send client emails from your own Gmail account." },
  { key: "carriers", label: "Carrier Integrations", icon: Plugs, blurb: "FedEx and UPS tracking connections and health checks." },
];
const LINKS = [
  { key: "users", to: "/users", label: "Users & Roles", icon: UsersThree },
  { key: "backups", to: "/backups", label: "Backups & Dropbox", icon: CloudArrowUp },
];
